/**
 * Reusable skeleton loading components for page transitions.
 * Replaces spinner with content-shaped placeholders that feel instant.
 */

function SkeletonPulse({ className = '' }) {
  return <div className={`animate-pulse bg-slate-200 rounded-xl ${className}`} />;
}

export function CardGridSkeleton({ count = 6, columns = 2 }) {
  return (
    <div className={`grid grid-cols-${columns} gap-3 px-3`}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-2xl overflow-hidden bg-white border border-slate-100">
          <SkeletonPulse className="h-36 w-full rounded-none" />
          <div className="p-3 space-y-2">
            <SkeletonPulse className="h-4 w-3/4" />
            <SkeletonPulse className="h-3 w-1/2" />
            <div className="flex gap-2 pt-1">
              <SkeletonPulse className="h-5 w-16 rounded-full" />
              <SkeletonPulse className="h-5 w-12 rounded-full" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function FilterBarSkeleton() {
  return (
    <div className="px-4 space-y-3">
      <div className="flex gap-2 overflow-hidden">
        {Array.from({ length: 7 }).map((_, i) => (
          <SkeletonPulse key={i} className="h-10 w-24 rounded-xl shrink-0" />
        ))}
      </div>
      <div className="flex gap-2 overflow-hidden">
        {Array.from({ length: 5 }).map((_, i) => (
          <SkeletonPulse key={i} className="h-8 w-20 rounded-full shrink-0" />
        ))}
      </div>
    </div>
  );
}

export function PageSkeleton({ navBar = true }) {
  return (
    <div className="animate-fade-in pb-24" data-testid="page-skeleton">
      <div className="px-4 pt-3 space-y-4">
        {navBar && (
          <div className="flex gap-2 overflow-hidden mb-4">
            {Array.from({ length: 7 }).map((_, i) => (
              <SkeletonPulse key={i} className="h-10 w-24 rounded-xl shrink-0" />
            ))}
          </div>
        )}
        <SkeletonPulse className="h-10 w-full" />
        <div className="flex gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <SkeletonPulse key={i} className="h-8 w-20 rounded-full shrink-0" />
          ))}
        </div>
      </div>
      <div className="mt-4">
        <CardGridSkeleton count={6} />
      </div>
    </div>
  );
}

export function RentalsSkeleton() {
  return (
    <div className="animate-fade-in" data-testid="rentals-skeleton">
      <div className="px-4 pt-3 mb-4">
        <div className="flex gap-2 overflow-hidden">
          {Array.from({ length: 7 }).map((_, i) => (
            <SkeletonPulse key={i} className="h-10 w-24 rounded-xl shrink-0" />
          ))}
        </div>
      </div>
      <SkeletonPulse className="h-48 w-full rounded-none" />
      <div className="max-w-6xl mx-auto px-4 mt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-2xl overflow-hidden bg-white border border-slate-100">
              <SkeletonPulse className="h-48 w-full rounded-none" />
              <div className="p-4 space-y-2">
                <SkeletonPulse className="h-5 w-3/4" />
                <SkeletonPulse className="h-3 w-1/2" />
                <div className="flex gap-3 pt-2">
                  <SkeletonPulse className="h-4 w-10" />
                  <SkeletonPulse className="h-4 w-10" />
                  <SkeletonPulse className="h-4 w-10" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function InsuranceSkeleton() {
  return (
    <div className="animate-fade-in pb-24" data-testid="insurance-skeleton">
      <div className="px-4 pt-3 space-y-4">
        <div className="flex gap-2 overflow-hidden mb-4">
          {Array.from({ length: 7 }).map((_, i) => (
            <SkeletonPulse key={i} className="h-10 w-24 rounded-xl shrink-0" />
          ))}
        </div>
        <SkeletonPulse className="h-10 w-full" />
        <div className="flex gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonPulse key={i} className="h-8 w-24 rounded-full shrink-0" />
          ))}
        </div>
      </div>
      <div className="px-3 mt-4 flex gap-3">
        <div className="flex-1 space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-2xl bg-white border border-slate-100 p-4 space-y-3">
              <div className="flex items-center gap-3">
                <SkeletonPulse className="h-10 w-10 rounded-full" />
                <div className="space-y-1 flex-1">
                  <SkeletonPulse className="h-4 w-2/3" />
                  <SkeletonPulse className="h-3 w-1/3" />
                </div>
              </div>
              <SkeletonPulse className="h-3 w-full" />
              <SkeletonPulse className="h-3 w-3/4" />
            </div>
          ))}
        </div>
        <div className="flex-1 space-y-3 pt-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-2xl bg-white border border-slate-100 p-4 space-y-3">
              <div className="flex items-center gap-3">
                <SkeletonPulse className="h-10 w-10 rounded-full" />
                <div className="space-y-1 flex-1">
                  <SkeletonPulse className="h-4 w-2/3" />
                  <SkeletonPulse className="h-3 w-1/3" />
                </div>
              </div>
              <SkeletonPulse className="h-3 w-full" />
              <SkeletonPulse className="h-3 w-3/4" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
